import { memoize } from 'lodash';
import { computed, ref, watch } from 'vue';

import { GlobalState, type ITenant } from '@/global';
import { DataCache } from '@/shared/data/cache';
import { hasPermission } from '@/shared/helpers/permissions.helpers';
import { Auth } from '@/shared/services/auth.service';

const mspTenantsRef = DataCache.getLazyRef('mspTenants');
const hasMspTenantPermission = computed(() => hasPermission('msp', 'login'));

/**
 * Whether this user has more than one tenant to reach: a member of several, an MSP with managed
 * tenants, or a platform admin. Impersonation counts on its own — the impersonated tenant's
 * permission set may not carry `msp.login`, but the user plainly came from somewhere else and can
 * go back (the tenant switcher makes the same allowance).
 *
 * Literally "can access more than one tenant", nothing more. Callers add their own conditions:
 * the tenant switcher entries also require not being on ZynoWorkstation, where the session is
 * pinned to a single tenant.
 */
export const canAccessMultipleTenants = computed(
    () =>
        (GlobalState.user.value?.tenants.length ?? 0) > 1 ||
        hasMspTenantPermission.value ||
        !!GlobalState.user.value?.isSysAdmin ||
        !!Auth.impersonatorTenantId.value
);

export const getAvailableTenantsRef = memoize(() => {
    const allTenantsRef = ref<Pick<ITenant, 'id' | 'name'>[]>();

    watch(
        [GlobalState.user, hasMspTenantPermission],
        async () => {
            if (!GlobalState.user.value) {
                allTenantsRef!.value = undefined;
                return;
            }

            if (hasMspTenantPermission.value) {
                await mspTenantsRef.load?.();
            }

            const userTenants = GlobalState.user.value.tenants;
            const userTenantIds = userTenants.map(t => t.id);

            const mspTenants = mspTenantsRef.value?.filter(t => !userTenantIds.includes(t.id)) || [];
            allTenantsRef.value = [...userTenants, ...mspTenants].sort((a, b) => a.name.localeCompare(b.name));
        },
        { immediate: true }
    );

    return allTenantsRef;
});

export const getAlternateTenantsRef = memoize(() =>
    computed(() => getAvailableTenantsRef().value?.filter(t => t.id !== GlobalState.tenant.value?.id))
);
